import { schemaResolver, type UseFormReturnType, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { captureMessage } from '@sentry/react';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { type RecoveryPlanSummary, useLoadRecoveryPlans } from 'modules/Settings/Workflows/api';
import { useEffect } from 'react';
import { filter, isNonNullish, isNullish, pipe, sortBy, take } from 'remeda';
import type {
  CompanyConfiguration,
  SendingMedium,
} from 'shared/hooks/use-load-company-configuration';
import { type Templates, useLoadTemplates } from 'shared/hooks/use-load-templates';
import {
  type StepsSwitch,
  UpdateClientPreferencesSchema,
} from 'shared/hooks/use-update-client-preferences';
import type { BoolanWithDefault } from 'types/boolean';
import type { Debtor } from 'types/store/client-state';

export type PreferenceFormValues = {
  sendToAllEmails: BoolanWithDefault;
  sendToContactPersonAndDebtorEmail: BoolanWithDefault;
  allowPaymentPlans: BoolanWithDefault;
  recoveryPlanId: string | null;
  defaultInvoiceTemplateId: string | null;
  defaultCreditNoteTemplateId: string | null;
  daysBetweenReminder: number;
  minimumSaldoSending: number | null;
  sendingMedium: Array<SendingMedium>;
  accountManagerId: string | null;
  stepsSwitch: StepsSwitch;
};

export type PreferenceForm = UseFormReturnType<PreferenceFormValues>;

const filterTemplatesByType = (templates: Templates | undefined, type: 'invoice' | 'credit_note') =>
  templates &&
  pipe(
    templates,
    filter(template => template.step_type === type),
  );

const getInitialRecoveryPlanId = (
  client: Debtor,
  company: CompanyConfiguration,
  recoveryPlans: Array<RecoveryPlanSummary>,
) => {
  if (isNullish(recoveryPlans)) return null;

  const clientPlanId = client?.recovery_plan?.id;
  const companyPlanId = company.recovery_plan?.id;

  const planId = clientPlanId ? String(clientPlanId) : companyPlanId;

  if (planId && recoveryPlans.some(plan => plan.id === planId)) {
    return planId;
  }

  return null;
};

const getInitialInvoiceTemplateId = (client: Debtor, company: CompanyConfiguration) => {
  const templateId = client?.default_invoice_template?.id ?? company.default_templates.invoice.id;
  return templateId ? String(templateId) : null;
};

const getInitialCreditNoteTemplateId = (client: Debtor, company: CompanyConfiguration) => {
  const templateId =
    client?.default_credit_note_template?.id ?? company.default_templates.credit_note.id;
  return templateId ? String(templateId) : null;
};

const isPostOrRegisteredPost = (medium: SendingMedium) =>
  medium.name === 'post' || medium.name === 'registered_post';

const arePostAndRegisteredPost = (mediums: Array<SendingMedium>) =>
  mediums.length === 2 && mediums.every(isPostOrRegisteredPost);

export const processSendingMedium = (sendingMedium: Array<SendingMedium>): Array<SendingMedium> => {
  const firstTwo = take(sendingMedium, 2);

  if (firstTwo.length !== 2) {
    return firstTwo;
  }

  if (arePostAndRegisteredPost(firstTwo)) {
    const mergedPostMedium = pipe(
      firstTwo,
      sortBy(medium => medium.order || 0),
      take(1),
    );

    const thirdItem = sendingMedium[2];
    return thirdItem ? [...mergedPostMedium, thirdItem] : mergedPostMedium;
  }

  return firstTwo;
};

export const usePreferenceForm = (client?: Debtor) => {
  const company = useCompanyConfiguration();

  const { recoveryPlans, isRecoveryPlansLoading } = useLoadRecoveryPlans();
  const { templates, isTemplatesLoading } = useLoadTemplates();
  const arePreferencesLoading = isRecoveryPlansLoading || isTemplatesLoading;

  const invoiceTemplates = filterTemplatesByType(templates, 'invoice');
  const creditNoteTemplates = filterTemplatesByType(templates, 'credit_note');

  const fallback: PreferenceFormValues = {
    accountManagerId: null,
    sendToAllEmails: 'default',
    sendToContactPersonAndDebtorEmail: 'default',
    allowPaymentPlans: 'default',
    recoveryPlanId: null,
    defaultInvoiceTemplateId: null,
    defaultCreditNoteTemplateId: null,
    daysBetweenReminder: 0,
    minimumSaldoSending: null,
    sendingMedium: [],
    stepsSwitch: [],
  };

  const form = useForm<PreferenceFormValues>({
    initialValues: fallback,
    validate: schemaResolver(UpdateClientPreferencesSchema, { sync: true }),
  });

  useEffect(() => {
    if (!client || !company || !recoveryPlans) return;

    const initialRecoveryPlanId = getInitialRecoveryPlanId(client, company, recoveryPlans);
    const initialInvoiceTemplateId = getInitialInvoiceTemplateId(client, company);
    const initialCreditNoteTemplateId = getInitialCreditNoteTemplateId(client, company);

    const preferenceFormValues: PreferenceFormValues = {
      accountManagerId: client.account_manager?.id || null,
      sendToAllEmails: isNonNullish(client.send_to_all_emails)
        ? (String(client.send_to_all_emails) as BoolanWithDefault)
        : 'default',
      sendToContactPersonAndDebtorEmail: isNonNullish(
        client.send_to_contact_person_and_debtor_email,
      )
        ? (String(client.send_to_contact_person_and_debtor_email) as BoolanWithDefault)
        : 'default',
      allowPaymentPlans: isNonNullish(client.allow_payment_plans)
        ? (String(client.allow_payment_plans) as BoolanWithDefault)
        : 'default',
      recoveryPlanId: initialRecoveryPlanId,
      defaultInvoiceTemplateId: initialInvoiceTemplateId,
      defaultCreditNoteTemplateId: initialCreditNoteTemplateId,
      daysBetweenReminder: client.days_between_reminder ?? 0,
      minimumSaldoSending: client.minimum_saldo_sending ?? null,
      sendingMedium: processSendingMedium(client.sending_media_attributes ?? []),
      stepsSwitch: [],
    };

    form.setValues(preferenceFormValues);
    form.resetDirty();
  }, [client?.id, company, recoveryPlans, templates]);

  const handleError = (errors: typeof form.errors) => {
    if (errors) {
      captureMessage(
        'Zod error while updating client preferences. Zod error attached to sentry event.',
        { extra: { zodError: errors } },
      );
      notifications.show({
        title: 'Error',
        message:
          'An error occurred while updating the client preferences. Our team has been notified.',
        color: 'red',
      });
    }
  };

  return {
    form,
    arePreferencesLoading,
    recoveryPlans,
    invoiceTemplates,
    sendingMedium: processSendingMedium(client?.sending_media_attributes ?? []),
    creditNoteTemplates,
    company,
    handleError,
  };
};
